'use client'
import {PaginatedChatRepository} from "@web-libs/features/chat/api/chat.repository";
import {useCreateChatConcern} from "@web-libs/features/chat/application/use-chat.concern";
import {AllMessagesRead, ChatroomOpened} from "@web-libs/features/chat/application/use-chat.events";
import {useEffect, useRef} from "react";
import {UseChatConcern} from "@web-libs/features/chat/application/use-chat.interface";

export const useCreatePaginatedChatConcern = (chatRepo: PaginatedChatRepository): UseChatConcern => {

    const currentPageNumber = useRef<number>(0);
    const lastPageReached = useRef<boolean>(false);
    const currentOpenChatroom = useRef<string | undefined>(undefined);
    const currentOpenChatroomLoading = useRef<boolean>(false);
    const isChatroomLoadingRef = useRef<(chatroomId: string) => boolean>((chatroomId) => false);

    const chatConcern: UseChatConcern = useCreateChatConcern(chatRepo, async (chatEvent) => {
        if (chatEvent instanceof AllMessagesRead) {
            if(!currentOpenChatroom.current || currentOpenChatroom.current !== chatEvent.chatroomId) return;
            if (lastPageReached.current || currentOpenChatroomLoading.current) return;
            _loadMoreMessages(chatEvent.chatroomId);
        } else if (chatEvent instanceof ChatroomOpened) {
            _resetCurrentRoomState();
        }
    });

    const _resetCurrentRoomState = () => {
        currentPageNumber.current = 0;
        lastPageReached.current = false;
    }

    const _loadMoreMessages = async (chatroomId: string) => {
        try {
            chatConcern.setChatroomLoading(chatroomId, true);
            const newMessages = await chatRepo.loadChatroomMessages(chatroomId, ++currentPageNumber.current);
            if (newMessages.last) lastPageReached.current = true;
            chatConcern.addChatroomMessages(chatroomId, newMessages.content);
        } catch (e) {
            console.error(e);
            lastPageReached.current = true;
        } finally {
            chatConcern.setChatroomLoading(chatroomId, false);
        }
    }

    useEffect(() => {
        currentOpenChatroom.current = chatConcern.currentOpenChatroom;
    }, [chatConcern.currentOpenChatroom]);

    useEffect(() => {
        currentOpenChatroomLoading.current = chatConcern.currentChatroomLoading;
    }, [chatConcern.currentChatroomLoading]);

    useEffect(() => {
        isChatroomLoadingRef.current = chatConcern.isChatroomLoading;
    }, [chatConcern.isChatroomLoading]);

    return chatConcern;
}
